import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  acknowledgeTrouble,
  getAlarmState,
  getBypass,
  getCockpit,
  getOutputs,
  getPgms,
  getQueue,
  getResources,
  getSession,
  getSiren,
  getTroubles,
  sendAlarmCommand,
  sendBypassCommand,
  sendPgmCommand,
  sendSirenCommand,
  type AlarmState,
  type BypassState,
  type CockpitState,
  type CommandResponse,
  type PgmItem,
  type PgmState,
  type QueueState,
  type RegistrySummary,
  type SessionResponse,
  type SirenState,
  type TroubleItem,
  type TroubleState,
} from "./api";
import {
  allAreasPostcondition,
  areaPostcondition,
  bypassPostcondition,
  pgmPostcondition,
  resolveConfirmedSnapshot,
  sirenPostcondition,
  troubleAcknowledgedPostcondition,
  type CommandPostcondition,
} from "./command-postconditions";
import {
  configureTelegramShell,
  confirmTelegramAction,
  notifyTelegramHaptic,
  releaseTelegramShell,
  showTelegramAlert,
} from "./telegram-compat";
import {
  commandsAreLocked,
  isOperationalReasonValid,
  NAVIGATION_TABS,
  type CommandPhase,
  type Connectivity,
  type Tab,
} from "./ui-contract";
import {
  isUiR8IconicEnabled,
  PersistentOperationalHeader,
  R8AutomationView,
  R8CockpitView,
  R8EventsView,
  R8NavigationIcon,
  R8SystemView,
  R8ZonesView,
  selectOperationalHeaderControls,
  selectTrustPresentation,
} from "./ui-r8";
import r14WordmarkUrl from "./ui-r14/assets/imersec-wordmark-dpr4.png";
import { isUiR14GoldEnabled } from "./ui-r14/feature-flag";
import "./styles.css";
import "./ui-r8/premium.css";
import "./ui-r14/r14.css";

const webApp = window.Telegram?.WebApp;
const searchParams = new URLSearchParams(window.location.search);
const isDemo = import.meta.env.DEV && searchParams.has("demo");
const demoScenario = isDemo ? (searchParams.get("demoState") ?? "healthy").trim().toLowerCase() : "healthy";
const demoTabCandidate = isDemo ? (searchParams.get("demoTab") ?? "HOME").trim().toUpperCase() : "HOME";
const demoInitialTab = NAVIGATION_TABS.some(({ id }) => id === demoTabCandidate)
  ? demoTabCandidate as Tab
  : "HOME";
const demoPhaseCandidate = isDemo ? (searchParams.get("demoPhase") ?? "IDLE").trim().toUpperCase() : "IDLE";
const demoInitialPhase: CommandPhase = (
  ["IDLE", "PRECHECKING", "EXECUTING", "CONFIRMED", "FAILED"] as const
).includes(demoPhaseCandidate as CommandPhase)
  ? demoPhaseCandidate as CommandPhase
  : "IDLE";
const demoPositionCandidate = isDemo ? (searchParams.get("demoPosition") ?? "TOP").trim().toUpperCase() : "TOP";
const demoPosition = ["TOP", "MIDDLE", "END"].includes(demoPositionCandidate)
  ? demoPositionCandidate as "TOP" | "MIDDLE" | "END"
  : "TOP";
const uiR8Iconic = isUiR8IconicEnabled();
const uiR14Gold = uiR8Iconic && isUiR14GoldEnabled();

function navigationLabel(tab: Tab, legacyLabel: string): string {
  if (!uiR8Iconic) return legacyLabel;
  if (tab === "HOME") return "Cockpit";
  if (tab === "SECURITY") return "Zonas";
  return legacyLabel;
}

interface Snapshot {
  alarm: AlarmState;
  cockpit: CockpitState;
  pgms: PgmState;
  siren: SirenState;
  bypass: BypassState;
  troubles: TroubleState;
  queue: QueueState;
  resources: RegistrySummary;
  outputs: RegistrySummary;
}

function askConfirmation(message: string): Promise<boolean> {
  if (isDemo) return Promise.resolve(true);
  return confirmTelegramAction(webApp, message, (fallbackMessage) => window.confirm(fallbackMessage));
}

function notifyHaptic(type: "error" | "success" | "warning"): void {
  notifyTelegramHaptic(webApp, type);
}

function showAlert(message: string): void {
  showTelegramAlert(webApp, message, (fallbackMessage) => window.alert(fallbackMessage));
}

function formatTime(value?: string | Date | null): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatBytes(bytes?: number): string {
  if (!Number.isFinite(bytes)) return "—";
  const gb = Number(bytes) / 1024 ** 3;
  return `${gb.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} GB`;
}

function phaseLabel(phase: CommandPhase): string {
  return {
    IDLE: "PRONTO",
    PRECHECKING: "VALIDANDO",
    EXECUTING: "EXECUTANDO",
    CONFIRMED: "CONFIRMADO",
    FAILED: "NÃO CONFIRMADO",
  }[phase];
}

function phaseDetail(phase: Exclude<CommandPhase, "IDLE">): string {
  return {
    PRECHECKING: "Validando autorização, conectividade e pré-condições antes do envio.",
    EXECUTING: "Aguardando confirmação autoritativa e avanço de versão.",
    CONFIRMED: "Pós-condição confirmada pelo ImerG.",
    FAILED: "A operação não foi confirmada; o estado anterior foi preservado.",
  }[phase];
}

function overallLabel(value: CockpitState["overall_status"]): string {
  return value === "HEALTHY" ? "Sistema saudável" : value === "DEGRADED" ? "Atenção necessária" : "Intervenção necessária";
}

function demoSnapshot(): { session: SessionResponse; snapshot: Snapshot } {
  const areas: AlarmState["areas"] = [
    { partition_id: "P1", area: "EXTERNAL", display_name: "Externo", state: "ARMED", transition: "NONE", health: "OK", button_action: "DISARM", button_label: "DESARMAR", zone_count: 14, zones: Array.from({ length: 14 }, (_, index) => index + 1) },
    { partition_id: "P2", area: "INTERNAL", display_name: "Interno", state: "ARMED", transition: "NONE", health: "OK", button_action: "DISARM", button_label: "DESARMAR", zone_count: 1, zones: [15] },
  ];
  const alarm: AlarmState = { state_version: 87, global_state: "ARMED_ALL", global_label: "Armado tudo", areas, incident_gate_enabled: true, updated_at_utc: new Date().toISOString() };
  const pgms: PgmState = { schema: "imerg.pgm-state.v1", physical_output_allowed: false, safe_state: "OFF", history_count: 1, pgms: (["PGM01", "PGM02", "PGM03"] as const).map((pgm_id, index) => ({ pgm_id, state: "OFF", pgm_version: index === 0 ? 2 : 0, auto_off_at_utc: null, reason: "INITIAL_SAFE_STATE", maximum_on_ms: 30000, physical_output: false })) };
  const siren: SirenState = { schema: "imerg.siren-state.v1", safe_state: "OFF", physical_output_allowed: false, history_count: 1, siren: { siren_id: "SIREN_MAIN", state: "OFF", siren_version: 2, auto_off_at_utc: null, reason: "AUTO_OFF", maximum_on_ms: 5000, physical_output: false } };
  const troubles: TroubleState = { schema: "imerg.trouble-state.v1", physical_output_allowed: false, independent_of_bypass: true, independent_of_arming: true, active: [], counts: { total: 3, open: 0, acknowledged: 0, resolved: 3 } };
  const registryBase = { revision: 1, mutation_enabled: false as const, states: { ENABLED: 21, DISABLED: 28, IDLE: 40 } };
  const cockpit = {
    schema: "imerg.operational-cockpit.v1", generated_at_utc: new Date().toISOString(), freshness: { status: "FRESH", max_age_seconds: 60 }, overall_status: "HEALTHY", reasons: [],
    core: { database_ok: true, replay_physical_output: "BLOCKED" }, incidents: { total: 374, by_status: { CLOSED: 374 } }, zone_actions: { total: 780, by_status: { COMPLETED: 374 }, by_decision: { TRIGGERED: 374 } }, bypass: { active_count: 0, history_count: 2 }, troubles: { counts: troubles.counts, active: [] }, queue: { counts: { COMPLETED: 100, DEAD_LETTER: 1 }, actionable_count: 0 },
    data_protection: { backup_freshness: "FRESH", backup_age_hours: 2.8, last_backup: { backup_id: "BKP-R30-DEMO", sha256: "DEMO", restore_drill_status: "PASSED" }, storage: { capacity_status: "OK", free_bytes: 779119734784 }, live_database_overwrite_allowed: false },
    protection_automation: { running: true, last_error: null, completed_jobs: 47 }, external_watchdog: { status: "HEALTHY", freshness: "FRESH", age_seconds: 18, restart_count_window: 0, restart_limit: 3 }, relay: { connected: true, all_off_confirmed: true, active_actions: 0 }, telegram: { ready: true, degraded: false, active_deliveries: 0, delivery_counts: { total: 63, by_status: { SENT: 61, UNCERTAIN: 2 } } },
  } as CockpitState;
  const bypass: BypassState = { schema: "imerg.zone-bypass-state.v1", mutation_scope: "LOGICAL_GATE_ONLY", physical_output_allowed: false, active: [], zones: [], history_count: 2 };

  if (demoScenario === "bypass") {
    const activeBypass = { zone: 1, status: "ACTIVE" as const, bypass_version: 3, bypass_until_utc: new Date(Date.now() + 30 * 60_000).toISOString(), reason: "Cenário visual local" };
    bypass.active = [activeBypass];
    bypass.zones = [activeBypass];
    cockpit.bypass.active_count = 1;
  } else if (demoScenario === "disarmed") {
    areas[1] = { ...areas[1], state: "DISARMED", button_action: "ARM", button_label: "ARMAR" };
    alarm.global_state = "PARTIAL";
    alarm.global_label = "Proteção parcial";
  } else if (demoScenario === "pgm-on") {
    pgms.pgms[0] = { ...pgms.pgms[0], state: "ON", pgm_version: pgms.pgms[0].pgm_version + 1, auto_off_at_utc: new Date(Date.now() + 30_000).toISOString(), reason: "Cenário visual local" };
  } else if (demoScenario === "siren-on") {
    siren.siren = { ...siren.siren, state: "ON", siren_version: siren.siren.siren_version + 1, auto_off_at_utc: new Date(Date.now() + 5_000).toISOString(), reason: "Cenário visual local" };
  } else if (demoScenario === "trouble") {
    const trouble: TroubleItem = {
      trouble_id: "TRB-R8-VISUAL-01",
      source_type: "CORE",
      resource_id: "IMERG-G1-CORE",
      code: "CORE_HEALTH_CRITICAL",
      severity: "CRITICAL",
      message: "Core requer intervenção",
      status: "OPEN",
      occurrence_count: 2,
      opened_at_utc: new Date().toISOString(),
      updated_at_utc: new Date().toISOString(),
    };
    troubles.active = [trouble];
    troubles.counts = { total: 4, open: 1, acknowledged: 0, resolved: 3 };
    cockpit.overall_status = "CRITICAL";
    cockpit.reasons = ["Falha crítica sintética para QA visual local"];
    cockpit.troubles = { counts: troubles.counts, active: [trouble] };
    cockpit.core.database_ok = false;
    cockpit.external_watchdog.status = "FAILED";
    cockpit.external_watchdog.freshness = "STALE";
    cockpit.relay.connected = false;
    cockpit.relay.all_off_confirmed = false;
    cockpit.telegram.degraded = true;
  } else if (demoScenario === "pending") {
    areas[0] = { ...areas[0], transition: "ARMING" };
  }

  return {
    session: {
      ok: true,
      user: { telegram_user_id: "6191158698", display_name: "Helvecio Oliveira", username: "" },
      role: demoScenario === "viewer" ? "VIEWER" : "ADMIN",
      permissions: demoScenario === "viewer" ? ["READ"] : ["READ", "OPERATE", "CONFIGURE"],
      site: { site_id: "IT01", display_name: "UFV IT01", telegram_group_id: "-1004328799954" },
      stage: "R32 LOCAL QA",
    },
    snapshot: {
      alarm, cockpit, pgms, siren,
      bypass,
      troubles,
      queue: { schema: "imerg.durable-queue-state.v1", physical_output_allowed: false, counts: { COMPLETED: 100, DEAD_LETTER: 1 }, recent: [] },
      resources: { ...registryBase, schema: "imerg.resource-registry.v1", registry_id: "IMERG-G1-IT01", sha256: "A1C80C…", resource_count: 89, kinds: { AREA: 2, CAMERA: 15, LINK: 1, MODULE: 1, ONT: 1, OPTICAL_CHANNEL: 15, PGM: 3, RELAY: 32, SIREN: 1, SITE: 1, SWITCH: 1, THERMAL_CHANNEL: 1, ZONE: 15 } },
      outputs: { ...registryBase, schema: "imerg.output-registry.v1", registry_id: "IMERG-G1-IT01-OUTPUTS", sha256: "81524B…", output_count: 32, functions: { IDLE: 13, PGM: 3, SIREN: 1, ZONE: 15 }, modes: { FOLLOW: 13, LATCH: 4, PULSE: 15 } },
    },
  };
}

function Metric({ label, value, tone = "neutral" }: { label: string; value: string | number; tone?: "neutral" | "ok" | "warn" | "critical" }) {
  return <div className={`metric metric--${tone}`}><strong>{value}</strong><span>{label}</span></div>;
}

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return <span className={`status-dot ${ok ? "status-dot--ok" : "status-dot--bad"}`}><i />{label}</span>;
}

function SectionHeader({ eyebrow, title, aside }: { eyebrow: string; title: string; aside?: React.ReactNode }) {
  return <div className="section-header"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div>{aside}</div>;
}

export default function App() {
  const [tab, setTab] = useState<Tab>(demoInitialTab);
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectivity, setConnectivity] = useState<Connectivity>("UPDATING");
  const [commandPhase, setCommandPhase] = useState<CommandPhase>(demoInitialPhase);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [reason, setReason] = useState("Teste operacional controlado R30");
  const [pgmDuration, setPgmDuration] = useState(uiR8Iconic ? 3000 : 5000);
  const [sirenDuration, setSirenDuration] = useState(uiR8Iconic ? 3000 : 1000);
  const operationLock = useRef(false);
  const snapshotRef = useRef<Snapshot | null>(null);

  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  const canOperate = session?.permissions.includes("OPERATE") ?? false;
  const commandsLocked = commandsAreLocked({
    canOperate,
    operationLocked: operationLock.current,
    phase: commandPhase,
    connectivity,
  });
  const activeBypass = useMemo(() => new Map(snapshot?.bypass.active.map((item) => [item.zone, item]) ?? []), [snapshot?.bypass.active]);
  const bypassVersions = useMemo(() => {
    const result = new Map<number, number>();
    for (const item of snapshot?.bypass.zones ?? []) result.set(item.zone, item.bypass_version);
    for (const item of snapshot?.bypass.active ?? []) result.set(item.zone, item.bypass_version);
    return result;
  }, [snapshot?.bypass.active, snapshot?.bypass.zones]);

  const refresh = useCallback(async (siteId?: string, quiet = false): Promise<Snapshot | null> => {
    const resolvedSiteId = siteId ?? session?.site.site_id;
    if (!resolvedSiteId) return null;
    if (isDemo) {
      const demo = demoSnapshot().snapshot;
      setSnapshot((current) => current ?? demo);
      setConnectivity("ONLINE");
      setLastRefresh(new Date());
      return snapshotRef.current ?? demo;
    }
    if (!quiet) setConnectivity("UPDATING");
    try {
      const [alarm, cockpit, pgms, siren, bypass, troubles, queue, resources, outputs] = await Promise.all([
        getAlarmState(resolvedSiteId), getCockpit(resolvedSiteId), getPgms(resolvedSiteId), getSiren(resolvedSiteId),
        getBypass(resolvedSiteId), getTroubles(resolvedSiteId), getQueue(resolvedSiteId), getResources(resolvedSiteId), getOutputs(resolvedSiteId),
      ]);
      const next = { alarm, cockpit, pgms, siren, bypass, troubles, queue, resources, outputs };
      setSnapshot(next);
      setConnectivity("ONLINE");
      setLastRefresh(new Date());
      setError(null);
      return next;
    } catch (nextError) {
      setConnectivity("OFFLINE");
      setError(nextError instanceof Error ? nextError.message : "Falha ao consultar o ImerG.");
      return null;
    }
  }, [session?.site.site_id]);

  useEffect(() => {
    configureTelegramShell(webApp);
    let active = true;
    (async () => {
      try {
        const initial = isDemo ? demoSnapshot() : null;
        const nextSession = initial?.session ?? await getSession();
        if (!active) return;
        setSession(nextSession);
        if (initial) {
          setSnapshot(initial.snapshot);
          setConnectivity("ONLINE");
          setLastRefresh(new Date());
        } else {
          await refresh(nextSession.site.site_id);
        }
      } catch (nextError) {
        if (active) {
          setConnectivity("OFFLINE");
          setError(nextError instanceof Error ? nextError.message : "Falha de autenticação.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
      releaseTelegramShell(webApp);
    };
  }, [refresh]);

  useEffect(() => {
    if (!session || isDemo) return;
    const hasTimedOutput = snapshot?.pgms.pgms.some((item) => item.state === "ON") || snapshot?.siren.siren.state === "ON";
    const timer = window.setInterval(() => {
      if (!operationLock.current && document.visibilityState === "visible") void refresh(undefined, true);
    }, hasTimedOutput ? 1000 : 5000);
    return () => window.clearInterval(timer);
  }, [refresh, session, snapshot?.pgms.pgms, snapshot?.siren.siren.state]);

  useEffect(() => {
    if (!isDemo || loading || !uiR14Gold) return;
    const frame = window.requestAnimationFrame(() => {
      const root = document.documentElement;
      const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
      const top = demoPosition === "MIDDLE" ? Math.round(maxScroll / 2) : demoPosition === "END" ? maxScroll : 0;
      window.scrollTo({ top, behavior: "auto" });
      root.dataset.r14QaPosition = demoPosition.toLowerCase();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, tab]);

  useEffect(() => {
    const online = () => { if (!operationLock.current) void refresh(); };
    const offline = () => setConnectivity("OFFLINE");
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => { window.removeEventListener("online", online); window.removeEventListener("offline", offline); };
  }, [refresh]);

  async function runCommand<T>(
    description: string,
    action: () => Promise<CommandResponse<T>>,
    postcondition: CommandPostcondition,
    applyConfirmedState: (current: Snapshot, confirmed: T) => Snapshot,
    demoMutation?: () => void,
  ) {
    if (!session || !snapshotRef.current || commandsLocked) return;
    if (!isOperationalReasonValid(reason)) {
      setError("Informe um motivo operacional com pelo menos 5 caracteres.");
      return;
    }
    const accepted = await askConfirmation(`${description}?\n\nMotivo: ${reason.trim()}\n\nA tela só confirma o que for relido do ImerG.`);
    if (!accepted) return;
    operationLock.current = true;
    setCommandPhase("PRECHECKING");
    setNotice("Validando versão, autorização e estado seguro.");
    setError(null);
    try {
      if (isDemo && demoMutation) {
        setCommandPhase("EXECUTING");
        demoMutation();
        await new Promise((resolve) => window.setTimeout(resolve, 50));
        if (!snapshotRef.current || !postcondition(snapshotRef.current)) {
          throw new Error("A simulação não atingiu a pós-condição esperada.");
        }
      } else {
        setCommandPhase("EXECUTING");
        setNotice("Comando assinado entregue. Aguardando readback autoritativo.");
        const response = await action();
        let confirmed = false;
        if (response.state_confirmation === "CONFIRMED" && response.confirmed_state && snapshotRef.current) {
          const responseSnapshot = resolveConfirmedSnapshot(
            snapshotRef.current,
            response.confirmed_state,
            applyConfirmedState,
            postcondition,
          );
          if (responseSnapshot) {
            snapshotRef.current = responseSnapshot;
            setSnapshot(responseSnapshot);
            setLastRefresh(new Date());
            confirmed = true;
          }
        }
        if (!confirmed) {
          for (let attempt = 0; attempt < 3; attempt += 1) {
            const authoritative = await refresh(session.site.site_id, true);
            if (authoritative && postcondition(authoritative)) {
              confirmed = true;
              break;
            }
            if (attempt < 2) {
              await new Promise((resolve) => window.setTimeout(resolve, 300 * (attempt + 1)));
            }
          }
        }
        if (!confirmed) {
          const detail = response.state_confirmation === "PENDING"
            ? "O Core aceitou o comando, mas a pós-condição continua pendente."
            : "O readback retornou sem comprovar o estado-alvo.";
          throw new Error(`${detail} A interface não alterou a confirmação.`);
        }
      }
      setCommandPhase("CONFIRMED");
      setNotice(`${description}: confirmado pelo ImerG.`);
      notifyHaptic("success");
      window.setTimeout(() => { setCommandPhase("IDLE"); setNotice(null); }, 5000);
    } catch (nextError) {
      const message = nextError instanceof Error ? nextError.message : "Comando não confirmado.";
      setCommandPhase("FAILED");
      setError(message);
      setNotice(null);
      notifyHaptic("error");
      showAlert(`COMANDO NÃO CONFIRMADO\n\n${message}`);
      window.setTimeout(() => setCommandPhase("IDLE"), 5000);
    } finally {
      operationLock.current = false;
    }
  }

  function mutateDemo(mutator: (current: Snapshot) => Snapshot) {
    setSnapshot((current) => {
      if (!current) return current;
      const next = mutator(current);
      snapshotRef.current = next;
      return next;
    });
    setLastRefresh(new Date());
  }

  function operateAll(target_state: "ARMED" | "DISARMED") {
    const current = snapshotRef.current;
    if (!current || !session) return;
    const label = target_state === "ARMED" ? "Armar todas as áreas" : "Desarmar todas as áreas";
    void runCommand(
      label,
      () => sendAlarmCommand(session.site.site_id, { action: "SET_ALL_AREAS", target_state, expected_state_version: current.alarm.state_version, reason }),
      allAreasPostcondition(target_state, current.alarm.state_version),
      (value, confirmed) => ({ ...value, alarm: confirmed }),
      () => mutateDemo((value) => ({ ...value, alarm: { ...value.alarm, state_version: value.alarm.state_version + 1, global_state: target_state === "ARMED" ? "ARMED_ALL" : "DISARMED_ALL", global_label: target_state === "ARMED" ? "Armado tudo" : "Desarmado tudo", areas: value.alarm.areas.map((area) => ({ ...area, state: target_state as "ARMED" | "DISARMED", button_action: target_state === "ARMED" ? "DISARM" as const : "ARM" as const, button_label: target_state === "ARMED" ? "DESARMAR" : "ARMAR" })) } })),
    );
  }

  function operateArea(area: AlarmState["areas"][number]) {
    const current = snapshotRef.current;
    if (!current || !session) return;
    const target_state: "ARMED" | "DISARMED" = area.state === "ARMED" ? "DISARMED" : "ARMED";
    const label = `${target_state === "ARMED" ? "Armar" : "Desarmar"} ${area.display_name}`;
    void runCommand(label, () => sendAlarmCommand(session.site.site_id, { action: "SET_AREA_STATE", area: area.area, target_state, expected_state_version: current.alarm.state_version, reason }), areaPostcondition(area.area, target_state, current.alarm.state_version), (value, confirmed) => ({ ...value, alarm: confirmed }), () => mutateDemo((value) => {
      const areas: AlarmState["areas"] = value.alarm.areas.map((item) => item.area === area.area ? { ...item, state: target_state, button_action: target_state === "ARMED" ? "DISARM" as const : "ARM" as const, button_label: target_state === "ARMED" ? "DESARMAR" : "ARMAR" } : item);
      const armed = areas.filter((item) => item.state === "ARMED").length;
      return { ...value, alarm: { ...value.alarm, areas, state_version: value.alarm.state_version + 1, global_state: armed === areas.length ? "ARMED_ALL" : armed === 0 ? "DISARMED_ALL" : "PARTIAL", global_label: armed === areas.length ? "Armado tudo" : armed === 0 ? "Desarmado tudo" : "Armado parcial" } };
    }));
  }

  function operateBypass(zone: number) {
    const current = snapshotRef.current;
    if (!current || !session) return;
    const active = activeBypass.has(zone);
    const action = active ? "RESTORE_ZONE" as const : "SET_ZONE_BYPASS" as const;
    const label = `${active ? "Restaurar" : "Aplicar bypass em"} Z${String(zone).padStart(2, "0")}`;
    const previousVersion = bypassVersions.get(zone) ?? 0;
    void runCommand(label, () => sendBypassCommand(session.site.site_id, { action, zone, expected_bypass_version: previousVersion, reason }), bypassPostcondition(zone, !active, previousVersion), (value, confirmed) => ({ ...value, bypass: confirmed }), () => mutateDemo((value) => {
      const version = previousVersion + 1;
      const row = { zone, status: active ? "RESTORED" as const : "ACTIVE" as const, bypass_version: version, reason, updated_at_utc: new Date().toISOString() };
      return { ...value, bypass: { ...value.bypass, zones: [...(value.bypass.zones ?? []).filter((item) => item.zone !== zone), row], active: active ? value.bypass.active.filter((item) => item.zone !== zone) : [...value.bypass.active.filter((item) => item.zone !== zone), row], history_count: value.bypass.history_count + 1 } };
    }));
  }

  function operatePgm(pgm: PgmItem) {
    const current = snapshotRef.current;
    if (!current || !session) return;
    const turningOn = pgm.state === "OFF";
    const label = turningOn ? `Executar ${pgm.pgm_id} por ${(pgmDuration / 1000).toLocaleString("pt-BR")} s` : `Desligar ${pgm.pgm_id}`;
    void runCommand(label, () => sendPgmCommand(session.site.site_id, { action: turningOn ? "PGM_ON" : "PGM_OFF", pgm_id: pgm.pgm_id, expected_pgm_version: pgm.pgm_version, ...(turningOn ? { duration_ms: pgmDuration } : {}), reason }), pgmPostcondition(pgm.pgm_id, turningOn ? "ON" : "OFF", pgm.pgm_version), (value, confirmed) => ({ ...value, pgms: confirmed }), () => {
      mutateDemo((value) => ({ ...value, pgms: { ...value.pgms, pgms: value.pgms.pgms.map((item) => item.pgm_id === pgm.pgm_id ? { ...item, state: turningOn ? "ON" : "OFF", pgm_version: item.pgm_version + 1, auto_off_at_utc: turningOn ? new Date(Date.now() + pgmDuration).toISOString() : null, reason } : item) } }));
      if (turningOn) window.setTimeout(() => mutateDemo((value) => ({ ...value, pgms: { ...value.pgms, pgms: value.pgms.pgms.map((item) => item.pgm_id === pgm.pgm_id ? { ...item, state: "OFF", pgm_version: item.pgm_version + 1, auto_off_at_utc: null, reason: "AUTO_OFF" } : item) } })), pgmDuration);
    });
  }

  function operateSiren() {
    const current = snapshotRef.current;
    if (!current || !session) return;
    const turningOn = current.siren.siren.state === "OFF";
    const label = turningOn ? `Acionar sirene por ${(sirenDuration / 1000).toLocaleString("pt-BR")} s` : "Desligar sirene";
    void runCommand(label, () => sendSirenCommand(session.site.site_id, { action: turningOn ? "SIREN_ON" : "SIREN_OFF", siren_id: "SIREN_MAIN", expected_siren_version: current.siren.siren.siren_version, ...(turningOn ? { duration_ms: sirenDuration } : {}), reason }), sirenPostcondition(turningOn ? "ON" : "OFF", current.siren.siren.siren_version), (value, confirmed) => ({ ...value, siren: confirmed }), () => {
      mutateDemo((value) => ({ ...value, siren: { ...value.siren, siren: { ...value.siren.siren, state: turningOn ? "ON" : "OFF", siren_version: value.siren.siren.siren_version + 1, auto_off_at_utc: turningOn ? new Date(Date.now() + sirenDuration).toISOString() : null, reason } } }));
      if (turningOn) window.setTimeout(() => mutateDemo((value) => ({ ...value, siren: { ...value.siren, siren: { ...value.siren.siren, state: "OFF", siren_version: value.siren.siren.siren_version + 1, auto_off_at_utc: null, reason: "AUTO_OFF" } } })), sirenDuration);
    });
  }

  function acknowledge(item: TroubleItem) {
    if (!session) return;
    void runCommand(
      `Reconhecer ${item.trouble_id}`,
      () => acknowledgeTrouble(session.site.site_id, item.trouble_id, reason),
      troubleAcknowledgedPostcondition(item.trouble_id),
      (value, confirmed) => ({ ...value, troubles: confirmed }),
    );
  }

  if (loading) return <main className="shell shell--center"><div className="loading-orb" /><p>Validando identidade e estado seguro…</p></main>;
  if (!session || !snapshot) return <main className="shell shell--center"><section className="fatal-panel"><span className="eyebrow">ACESSO NÃO LIBERADO</span><h1>ImerG indisponível</h1><p>{error ?? "Abra pelo menu privado do bot ImerG."}</p></section></main>;

  const { alarm, cockpit, pgms, siren, troubles, queue, resources, outputs } = snapshot;
  const allArmed = alarm.global_state === "ARMED_ALL";
  const allDisarmed = alarm.global_state === "DISARMED_ALL";
  const r8HeaderControls = selectOperationalHeaderControls({ alarm, siren });
  const r8Trust = selectTrustPresentation(connectivity === "ONLINE" ? cockpit : null);
  function navigateToTab(nextTab: Tab) {
    setTab(nextTab);
    window.scrollTo({ top: 0, behavior: uiR8Iconic ? "auto" : "smooth" });
  }

  return (
    <main
      className={`shell${uiR8Iconic ? " shell--r8 shell--r8-premium" : ""}${uiR14Gold ? " shell--r14" : ""}`}
      data-ui-visual={uiR8Iconic ? "r8-premium-v1" : "legacy"}
      data-ui-style={uiR14Gold ? "r14-gold" : undefined}
      data-command-phase={uiR14Gold ? commandPhase.toLowerCase() : undefined}
      data-access={uiR14Gold ? canOperate ? "operator" : "viewer" : undefined}
    >
      {uiR8Iconic ? (
        <PersistentOperationalHeader
          plantName={session.site.display_name}
          trust={r8Trust}
          controls={r8HeaderControls}
          wordmarkUrl={uiR14Gold ? r14WordmarkUrl : undefined}
          semanticControls={uiR14Gold}
          onBrandSelect={() => navigateToTab("HOME")}
          onControlSelect={(control) => {
            if (control.id === "external" || control.id === "internal") navigateToTab("SECURITY");
            if (control.id === "floodlights" || control.id === "camera-alert" || control.id === "industrial-siren") navigateToTab("AUTOMATION");
          }}
        />
      ) : <header className="app-header">
        <button className="brand" type="button" onClick={() => setTab("HOME")} aria-label="Ir para visão geral"><span>IM</span><div><strong>IMERG</strong><small>{session.site.display_name}</small></div></button>
        <div className="header-status">
          <span className={`live-dot live-dot--${connectivity.toLowerCase()}`}><i />{connectivity}</span>
          <button type="button" className="avatar" onClick={() => setTab("SYSTEM")} aria-label="Abrir perfil e sistema">{session.user.display_name.slice(0, 1).toUpperCase()}</button>
        </div>
      </header>}

      {uiR14Gold && !canOperate ? (
        <section className="r14-access-banner" role="status" aria-live="polite">
          <span aria-hidden="true">◈</span>
          <div><strong>SOMENTE LEITURA</strong><small>Comandos indisponíveis para este perfil; estados e readback permanecem ativos.</small></div>
        </section>
      ) : null}

      {uiR14Gold && commandPhase !== "IDLE" ? (
        <section className="r14-command-state" data-phase={commandPhase.toLowerCase()} role="status" aria-live="assertive">
          <i aria-hidden="true" />
          <div>
            <strong>{phaseLabel(commandPhase)}</strong>
            <small>{phaseDetail(commandPhase)}</small>
          </div>
        </section>
      ) : null}

      <section className={uiR8Iconic ? "r8-sr-only" : "command-rail"} aria-live="polite" aria-atomic="true">
        <span>{phaseLabel(commandPhase)}</span><i />
        <span>Readback {formatTime(lastRefresh)}</span><i />
        <span>v{alarm.state_version}</span>
      </section>

      {notice && <div className={`notice notice--${commandPhase.toLowerCase()}`} role="status" aria-live="polite">{notice}</div>}
      {error && <div className="notice notice--failed" role="alert" aria-live="assertive"><span>{error}</span><button type="button" onClick={() => { setError(null); void refresh(); }}>TENTAR NOVAMENTE</button></div>}

      <div className="view" key={tab}>
        {tab === "HOME" && (
          uiR8Iconic ? (
            <R8CockpitView
              alarm={alarm}
              cockpit={cockpit}
              pgms={pgms}
              siren={siren}
              bypass={snapshot.bypass}
              troubles={troubles}
              r14Mode={uiR14Gold}
              onNavigate={navigateToTab}
            />
          ) : <>
            <section className={`hero hero--${cockpit.overall_status.toLowerCase()}`}>
              <div className="hero__top"><span className="eyebrow">ESTADO AUTORITATIVO</span><span className="hero__status"><i />{cockpit.overall_status}</span></div>
              <h1>{overallLabel(cockpit.overall_status)}</h1>
              <p>{alarm.global_label} · Todas as decisões continuam locais no Core IT01.</p>
              <div className="hero__checks"><StatusDot ok={cockpit.core.database_ok} label="SQLite" /><StatusDot ok={cockpit.relay.connected && (cockpit.relay.output_state_confirmed ?? cockpit.relay.all_off_confirmed)} label={(cockpit.relay.latched_relays?.length ?? 0) > 0 ? "Saídas confirmadas" : "Relés ALL OFF"} /><StatusDot ok={cockpit.external_watchdog.status === "HEALTHY"} label="Watchdog" /><StatusDot ok={cockpit.telegram.ready && !cockpit.telegram.degraded} label="Telegram" /></div>
            </section>

            <section className="metrics-grid"><Metric label="Incidentes" value={cockpit.incidents.total} /><Metric label="Troubles abertos" value={troubles.counts.open} tone={troubles.counts.open ? "critical" : "ok"} /><Metric label="Bypasses" value={snapshot.bypass.active.length} tone={snapshot.bypass.active.length ? "warn" : "ok"} /><Metric label="Fila acionável" value={cockpit.queue.actionable_count} tone={cockpit.queue.actionable_count ? "warn" : "ok"} /></section>

            <section className="panel">
              <SectionHeader eyebrow="SEGURANÇA" title={alarm.global_label} aside={<button className="text-action" type="button" onClick={() => setTab("SECURITY")}>ABRIR</button>} />
              <div className="area-summary">{alarm.areas.map((area) => <div key={area.area}><span>{area.display_name}</span><strong className={area.state === "ARMED" ? "text-ok" : "text-warn"}>{area.state === "ARMED" ? "ARMADO" : "DESARMADO"}</strong></div>)}</div>
            </section>

            <section className="panel">
              <SectionHeader eyebrow="AUTOMAÇÃO" title="Saídas controladas" aside={<button className="text-action" type="button" onClick={() => setTab("AUTOMATION")}>OPERAR</button>} />
              <div className="output-summary"><div><span>PGM</span><strong>{pgms.pgms.filter((item) => item.state === "ON").length ? "ATIVA" : "TODAS OFF"}</strong></div><div><span>SIRENE</span><strong>{siren.siren.state}</strong></div><span className={`simulation-badge ${pgms.physical_output_allowed ? "simulation-badge--physical" : ""}`}>{pgms.physical_output_allowed ? "R29–R32 FÍSICO" : "SIMULADO"}</span></div>
            </section>
          </>
        )}

        {tab === "SECURITY" && (
          uiR8Iconic ? (
            <R8ZonesView
              alarm={alarm}
              bypass={snapshot.bypass}
              reason={reason}
              canOperate={canOperate}
              commandsLocked={commandsLocked}
              onReasonChange={setReason}
              onOperateAll={operateAll}
              onOperateArea={operateArea}
              onOperateBypass={operateBypass}
              onRefresh={() => void refresh()}
              r14Mode={uiR14Gold}
            />
          ) : <>
            <SectionHeader eyebrow="PROTEÇÃO" title="Áreas e zonas" aside={<button className="icon-button" type="button" onClick={() => void refresh()} aria-label="Atualizar">↻</button>} />
            <section className="state-hero"><div><span>ESTADO GLOBAL</span><strong>{alarm.global_label}</strong><small>Versão {alarm.state_version} · Gate de incidente {alarm.incident_gate_enabled ? "ativo" : "inativo"}</small></div><i className={allArmed ? "armed" : allDisarmed ? "disarmed" : "partial"} /></section>
            {canOperate && <div className="split-actions"><button type="button" className="primary primary--safe" disabled={commandsLocked || allArmed} onClick={() => operateAll("ARMED")}>ARMAR TUDO</button><button type="button" className="primary primary--danger" disabled={commandsLocked || allDisarmed} onClick={() => operateAll("DISARMED")}>DESARMAR TUDO</button></div>}
            <div className="control-grid control-grid--areas">{alarm.areas.map((area) => <button type="button" className={`control-tile control-tile--${area.state.toLowerCase()}`} key={area.area} disabled={!canOperate || commandsLocked} onClick={() => operateArea(area)} aria-label={`${area.partition_id} ${area.display_name}: ${area.state === "ARMED" ? "armada; tocar para desarmar" : "desarmada; tocar para armar"}`} title={`${area.display_name} · ${area.state}`}><span>{area.partition_id}</span><i aria-hidden="true" /></button>)}</div>

            <section className="panel panel--flush">
              <SectionHeader eyebrow="BYPASS LÓGICO" title={`${snapshot.bypass.active.length} ativo${snapshot.bypass.active.length === 1 ? "" : "s"}`} aside={<span className="simulation-badge">NÃO ACIONA RELÉ</span>} />
              <p className="helper">Bypass é somente lógico e não aciona relé. R01–R15 pulsam apenas por incidente confirmado no Core local; Trouble continua independente.</p>
              <div className="zone-grid">{Array.from({ length: 15 }, (_, index) => index + 1).map((zone) => {
                const active = activeBypass.has(zone);
                return <button type="button" key={zone} className={`control-tile control-tile--zone ${active ? "control-tile--bypassed" : "control-tile--normal"}`} disabled={!canOperate || commandsLocked} onClick={() => operateBypass(zone)} aria-label={`Zona ${zone}: ${active ? "bypass ativo; tocar para restaurar" : "normal; tocar para aplicar bypass"}`} title={`Z${String(zone).padStart(2, "0")} · ${active ? "BYPASS" : "NORMAL"}`}><span>{String(zone).padStart(2, "0")}</span><i aria-hidden="true" /></button>;
              })}</div>
            </section>
          </>
        )}

        {tab === "AUTOMATION" && (
          uiR8Iconic ? (
            <R8AutomationView
              pgms={pgms}
              siren={siren}
              reason={reason}
              pgmDuration={pgmDuration}
              sirenDuration={sirenDuration}
              canOperate={canOperate}
              commandsLocked={commandsLocked}
              onReasonChange={setReason}
              onPgmDurationChange={setPgmDuration}
              onSirenDurationChange={setSirenDuration}
              onOperatePgm={operatePgm}
              onOperateSiren={operateSiren}
              r14Mode={uiR14Gold}
            />
          ) : <>
            <SectionHeader eyebrow="AUTOMAÇÃO" title="Saídas" aside={<span className={`simulation-badge ${pgms.physical_output_allowed ? "simulation-badge--physical" : ""}`}>{pgms.physical_output_allowed ? "FÍSICO · READBACK" : "SIMULADO"}</span>} />
            <div className={`safety-callout ${pgms.physical_output_allowed ? "safety-callout--physical" : ""}`}><strong>{pgms.physical_output_allowed ? "PGM R29–R31 e sirene R32 sob comando físico." : "Modo de laboratório sem saída física."}</strong><span>{pgms.physical_output_allowed ? "Zonas R01–R15 são automáticas pelo Core local. Tiles usam readback e auto-OFF obrigatório." : "Tiles simulam estado com os mesmos limites e confirmação."}</span></div>
            <label className="field"><span>Motivo operacional</span><input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} placeholder="Obrigatório para auditoria" /></label>

            <section className="compact-toolbar">
              <div><span>PGM</span><div className="segmented segmented--inline">{[1000, 5000, 15000, 30000].map((duration) => <button type="button" key={duration} className={pgmDuration === duration ? "active" : ""} onClick={() => setPgmDuration(duration)}>{duration / 1000}s</button>)}</div></div>
              <div><span>SIRENE</span><div className="segmented segmented--inline segmented--five">{[250, 500, 1000, 3000, 5000].map((duration) => <button type="button" key={duration} className={sirenDuration === duration ? "active" : ""} onClick={() => setSirenDuration(duration)}>{duration < 1000 ? `${duration}m` : `${duration / 1000}s`}</button>)}</div></div>
            </section>

            <div className="control-grid control-grid--outputs">
              {pgms.pgms.map((pgm) => <button type="button" key={pgm.pgm_id} className={`control-tile control-tile--output control-tile--${pgm.state.toLowerCase()}`} disabled={!canOperate || commandsLocked} onClick={() => operatePgm(pgm)} aria-label={`${pgm.pgm_id}: ${pgm.state}; ${pgm.state === "ON" ? "tocar para desligar" : `tocar para acionar por ${pgmDuration / 1000} segundos`}`} title={`${pgm.pgm_id} · ${pgm.state} · R${(pgms.relay_map?.[pgm.pgm_id] ?? Number(pgm.pgm_id.slice(-2)) + 28).toString().padStart(2, "0")}`}><span>{pgm.pgm_id.slice(-2)}</span><i aria-hidden="true" /></button>)}
              <button type="button" className={`control-tile control-tile--output control-tile--siren control-tile--${siren.siren.state.toLowerCase()}`} disabled={!canOperate || commandsLocked} onClick={operateSiren} aria-label={`Sirene: ${siren.siren.state}; ${siren.siren.state === "ON" ? "tocar para desligar" : `tocar para acionar por ${sirenDuration / 1000} segundos`}`} title={`SIREN_MAIN · ${siren.siren.state} · R32`}><span>S</span><i aria-hidden="true" /></button>
            </div>
          </>
        )}

        {tab === "EVENTS" && (
          uiR8Iconic ? (
            <R8EventsView
              troubles={troubles}
              queue={queue}
              reason={reason}
              canOperate={canOperate}
              commandsLocked={commandsLocked}
              onReasonChange={setReason}
              onAcknowledge={acknowledge}
              r14Mode={uiR14Gold}
            />
          ) : <>
            <SectionHeader eyebrow="EVENTOS" title="Trouble e atividade" aside={<span className={`count-badge ${troubles.counts.open ? "count-badge--critical" : ""}`}>{troubles.counts.open} ABERTOS</span>} />
            <section className="metrics-grid"><Metric label="Abertos" value={troubles.counts.open} tone={troubles.counts.open ? "critical" : "ok"} /><Metric label="Reconhecidos" value={troubles.counts.acknowledged} /><Metric label="Resolvidos" value={troubles.counts.resolved} tone="ok" /><Metric label="Incidentes" value={cockpit.incidents.total} /></section>
            {troubles.active.length === 0 ? <section className="empty-state"><div>✓</div><h3>Nenhum Trouble ativo</h3><p>Hardware, storage, watchdog e integrações sem anomalias abertas.</p></section> : <div className="trouble-list">{troubles.active.map((item) => <section key={item.trouble_id} className={`trouble trouble--${item.severity.toLowerCase()}`}><div><span>{item.severity} · {item.resource_id}</span><h3>{item.message}</h3><p>{item.code} · {item.occurrence_count} ocorrência(s)</p></div>{item.status === "OPEN" && canOperate && <button type="button" className="secondary" disabled={commandsLocked} onClick={() => acknowledge(item)}>RECONHECER</button>}</section>)}</div>}
            <section className="panel"><SectionHeader eyebrow="FILA DURÁVEL" title={`${cockpit.queue.actionable_count} acionável`} /><div className="key-values">{Object.entries(queue.counts).map(([key, value]) => <div key={key}><span>{key}</span><strong>{value}</strong></div>)}</div></section>
          </>
        )}

        {tab === "SYSTEM" && (
          uiR8Iconic ? (
            <R8SystemView
              cockpit={cockpit}
              resources={resources}
              outputs={outputs}
              userName={session.user.display_name}
              role={session.role}
              siteId={session.site.site_id}
              stage={session.stage}
              onRefresh={() => void refresh()}
            />
          ) : <>
            <SectionHeader eyebrow="SISTEMA" title="Saúde e inventário" aside={<button className="icon-button" type="button" onClick={() => void refresh()} aria-label="Atualizar">↻</button>} />
            <section className="profile-card"><div className="profile-card__avatar">{session.user.display_name.slice(0, 1).toUpperCase()}</div><div><h3>{session.user.display_name}</h3><p>Telegram · {session.role} · {session.site.site_id}</p></div><span>{session.stage}</span></section>
            <section className="health-list"><div><StatusDot ok={cockpit.external_watchdog.status === "HEALTHY"} label="Watchdog externo" /><strong>{cockpit.external_watchdog.freshness} · {Math.round(cockpit.external_watchdog.age_seconds)}s</strong></div><div><StatusDot ok={cockpit.data_protection.backup_freshness === "FRESH"} label="Backup e restore" /><strong>{cockpit.data_protection.backup_freshness} · drill {cockpit.data_protection.last_backup?.restore_drill_status ?? "—"}</strong></div><div><StatusDot ok={cockpit.protection_automation.running && !cockpit.protection_automation.last_error} label="Automação de proteção" /><strong>{cockpit.protection_automation.completed_jobs} jobs</strong></div><div><StatusDot ok={cockpit.relay.connected && (cockpit.relay.output_state_confirmed ?? cockpit.relay.all_off_confirmed)} label="Módulo de relés" /><strong>{(cockpit.relay.latched_relays?.length ?? 0) > 0 ? `LATCH R${cockpit.relay.latched_relays?.join(", R")}` : "Conectado · ALL OFF"}</strong></div><div><StatusDot ok={cockpit.telegram.ready && !cockpit.telegram.degraded} label="Telegram" /><strong>{cockpit.telegram.delivery_counts.total} entregas</strong></div><div><StatusDot ok={cockpit.data_protection.storage.capacity_status === "OK"} label="Storage" /><strong>{formatBytes(cockpit.data_protection.storage.free_bytes)} livres</strong></div></section>
            <section className="registry-grid"><div className="registry-card"><span>RESOURCE REGISTRY</span><strong>{resources.resource_count ?? 0}</strong><small>recursos · {Object.keys(resources.kinds ?? {}).length} tipos</small><code>{resources.sha256}</code></div><div className="registry-card"><span>OUTPUT REGISTRY</span><strong>{outputs.output_count ?? 0}</strong><small>saídas · mutação bloqueada</small><code>{outputs.sha256}</code></div></section>
            <section className="panel panel--flush"><SectionHeader eyebrow="MAPA R01–R32" title="Funções canônicas" aside={<span className="simulation-badge">SOMENTE LEITURA</span>} /><div className="output-map">{Array.from({ length: 32 }, (_, index) => index + 1).map((relay) => { const target = relay <= 15 ? `Z${String(relay).padStart(2, "0")}` : relay <= 28 ? "IDLE" : relay <= 31 ? `PGM0${relay - 28}` : "SIREN"; return <div key={relay}><span>R{String(relay).padStart(2, "0")}</span><strong>{target}</strong><small>{relay <= 15 ? "PULSE · AUTO" : relay <= 28 ? "FOLLOW · OFF" : "LATCH · CMD"}</small></div>; })}</div></section>
          </>
        )}
      </div>

      {!uiR8Iconic && tab !== "AUTOMATION" && <label className="reason-dock"><span>Motivo para o próximo comando</span><input value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} /></label>}

      <nav className="bottom-nav" aria-label="Navegação principal">{NAVIGATION_TABS.map((item) => <button type="button" key={item.id} className={tab === item.id ? "active" : ""} aria-current={tab === item.id ? "page" : undefined} onClick={() => navigateToTab(item.id)}><span aria-hidden="true">{uiR8Iconic ? <R8NavigationIcon tab={item.id} /> : item.glyph}</span><small>{navigationLabel(item.id, item.label)}</small>{item.id === "EVENTS" && troubles.counts.open > 0 && <i>{troubles.counts.open}</i>}</button>)}</nav>
    </main>
  );
}
